import { useEffect, useState } from "react";
import type { VisitDetail, VisitSummary } from "@dentacore/shared-types";
import { ATTACHMENT_KIND_LABELS, INVOICE_STATUS_LABELS } from "@dentacore/shared-types";

import { completeVisit, getVisit } from "../api/patients";
import { useCan } from "../auth/use-can";
import { AddWorkForm } from "./add-work-form";
import { formatMoney } from "./money";
import styles from "./visit-dialog.module.css";

/**
 * One visit, tied together: the note written that day, the invoice raised and
 * the images taken — so a clinician reads a past visit as a visit rather than as
 * three separate lists.
 *
 * It was the modal of *Recent visits*, the list the mockup puts under the
 * chart. That list is gone (PF5, 2026-09-27): a day's visit now opens from its
 * container on the History tab, and from *Last visits* on the Chart tab. The
 * dialog is unchanged — what it shows, and *Complete visit and bill it*.
 *
 * ── The dates are the server's ──────────────────────────────────────────────
 *
 * **Nothing here computes a date.** Each visit arrives already resolved in the
 * timezone of the branch it happened at (P2 Q8), and this renders that string.
 * Re-deriving *which day* it was from a timestamp in the browser is exactly
 * what P2 Q8 rejected: it lets one visit show two dates to two members of staff.
 */
export function VisitDialog({
  patientId,
  date,
  onClose,
  onOpenImage,
  onChanged,
}: {
  patientId: string;
  /** The visit's local date, `2026-01-05`. */
  date: string;
  onClose: () => void;
  /** Hands an image to the Imaging tab's viewer (client's manual QA, item 29). */
  onOpenImage?: (attachmentId: string) => void;
  /** Told when *Complete visit and bill it* changed the visit, so lists can refresh. */
  onChanged?: () => void;
}): React.JSX.Element | null {
  const [open, setOpen] = useState<VisitDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingWork, setAddingWork] = useState(false);
  // A section the reader may not open is not drawn at all (QF-D18): the server
  // sends it empty, and "Nothing was billed" would then be untrue.
  const can = useCan();

  useEffect(() => {
    const controller = new AbortController();
    setOpen(null);
    setError(null);
    getVisit(patientId, date, controller.signal)
      .then(setOpen)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not open that visit.");
      });
    return () => controller.abort();
  }, [patientId, date]);

  if (open === null) {
    // The error has nowhere else to go while nothing has loaded, so it is the
    // dialog: a visit that cannot be opened says so rather than doing nothing.
    return error === null ? null : (
      <div className={styles.backdrop} role="dialog" aria-modal="true" data-testid="visit-modal">
        <div className={styles.modal}>
          <p className={styles.error} data-testid="visits-error">
            {error}
          </p>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
            data-testid="visit-close"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`Visit on ${formatDate(open.date)}`}
      data-testid="visit-modal"
    >
      <div className={styles.modal}>
        <div className={styles.modalHead}>
          <div>
            <div className={styles.modalDate} data-testid="visit-modal-date">
              {formatDate(open.date)}
              {open.providerName === null ? "" : ` · ${open.providerName}`}
            </div>
            <h3 className={styles.modalTitle}>{open.title ?? "Visit"}</h3>
            {/*
              **The facts about the visit as chips, not as a sentence.** One muted
              line of two unrelated facts joined by a dot was most of why the
              dialog read as *"endless rows of text"*. Which timezone decided the
              date is stated rather than assumed: a clinic whose branches span
              timezones is exactly the case where a reader would wonder.
            */}
            <div className={styles.modalMeta}>
              <span className={styles.fact}>{open.branchName ?? "No branch recorded"}</span>
              <span className={styles.fact}>{open.timezone}</span>
              {open.providerName !== null && (
                <span className={styles.fact}>{open.providerName}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
            data-testid="visit-close"
          >
            Close
          </button>
        </div>

        {error !== null && (
          <p className={styles.error} data-testid="visits-error">
            {error}
          </p>
        )}

        {(open.notes.length > 0 || can("clinical.notes.view")) && (
          <Section title="Clinical note" empty="No note was written that day.">
            {open.notes.map((note) => (
              <div key={note.id} className={styles.record} data-testid="visit-note">
                <div className={styles.recordHead}>
                  <span className={styles.recordTitle}>{note.title ?? "Untitled note"}</span>
                  <span className={styles.recordBy}>{note.authorName ?? ""}</span>
                </div>
                <p className={styles.body}>{note.body}</p>
              </div>
            ))}
          </Section>
        )}

        {(open.invoices.length > 0 || can("billing.view")) && (
          <Section title="Invoice for this visit" empty="Nothing was billed for that day.">
            {open.invoices.map((invoice) => (
              <div key={invoice.id} className={styles.row} data-testid="visit-invoice">
                <span className={styles.rowNumber}>{invoice.number}</span>
                <span className={styles.rowWhat}>
                  {invoice.lines.map((line) => line.description).join(" · ")}
                </span>
                <span className={styles.rowAmount}>
                  {formatMoney(invoice.totalCents, open.currency)}
                </span>
                <span className={styles.pill}>{INVOICE_STATUS_LABELS[invoice.status]}</span>
              </div>
            ))}
          </Section>
        )}

        {(open.images.length > 0 || can("clinical.imaging.view")) && (
          <Section title="Images from this visit" empty="No images were taken that day.">
            {open.images.map((image) => (
              /*
              **A button, not a div** (client's manual QA, item 29): it opens the
              real viewer on the Imaging tab, where downloading and deleting live.
            */
              <button
                key={image.id}
                type="button"
                className={`${styles.row} ${styles.rowOpens}`}
                onClick={() => onOpenImage?.(image.id)}
                disabled={onOpenImage === undefined}
                data-card=""
                data-testid="visit-image"
              >
                <span className={styles.thumb} aria-hidden="true">
                  {image.thumbnailUrl === null ? null : (
                    <img src={image.thumbnailUrl} alt="" className={styles.thumbImg} />
                  )}
                </span>
                <span className={styles.rowWhat}>{ATTACHMENT_KIND_LABELS[image.kind]}</span>
                <span className={styles.rowBy}>{regionLabel(image.region)}</span>
              </button>
            ))}
          </Section>
        )}

        {/*
          **The one place in Phase 2 that can finish a visit** (P2 Q15), offered
          only for a visit in the chair — `POST :id/complete` goes through the
          status machine since STORY-037, and offering it on anything else would
          be a click that always fails.
        */}
        {open.appointmentId !== null && open.appointmentStatus === "IN_CHAIR" && (
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => {
                const { appointmentId } = open;
                if (appointmentId === null) return;
                setBusy(true);
                setError(null);
                completeVisit(appointmentId)
                  .then(() => getVisit(patientId, date))
                  .then((next) => {
                    setOpen(next);
                    onChanged?.();
                  })
                  .catch((cause: unknown) => {
                    setError(
                      cause instanceof Error ? cause.message : "Could not complete that visit.",
                    );
                  })
                  .finally(() => setBusy(false));
              }}
              data-testid="complete-visit"
            >
              Complete visit and bill it
            </button>
            <span className={styles.hint}>
              Raises an invoice for the work charted that day. Nothing charted, no invoice.
            </span>
          </div>
        )}

        {/*
          **Add work done** (EB7) — the finished visit's counterpart to the
          button above. Offered only once the visit is completed; before that,
          work is recorded when the visit is completed.
        */}
        {open.appointmentId !== null &&
          open.appointmentStatus === "COMPLETED" &&
          (addingWork ? (
            <AddWorkForm
              patientId={patientId}
              appointmentId={open.appointmentId}
              currency={open.currency}
              onCancel={() => setAddingWork(false)}
              onDone={() => {
                setAddingWork(false);
                void getVisit(patientId, date)
                  .then(setOpen)
                  .catch(() => {
                    // The work is saved; a failed re-read only leaves this stale.
                  });
                onChanged?.();
              }}
            />
          ) : (
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setAddingWork(true)}
                data-testid="add-work"
              >
                Add work done
              </button>
              <span className={styles.hint}>
                Something else done at this visit: recorded and billed in one step.
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const filled = Array.isArray(children) ? children.length > 0 : children !== null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {filled ? children : <p className={styles.note}>{empty}</p>}
    </div>
  );
}

/**
 * The data a visit row carries for specs: what the server derived is on this
 * date, so a test can assert that opening it shows exactly these and no others.
 */
export function visitRowData(visit: VisitSummary): Record<string, string | number> {
  return {
    "data-visit-date": visit.date,
    "data-notes": visit.noteCount,
    "data-images": visit.imageCount,
    "data-invoices": visit.invoiceCount,
  };
}

/** `2 notes · 1 image`, or "Nothing recorded" when a visit has only its appointment. */
export function countsOf(visit: VisitSummary): string {
  const parts = [
    plural(visit.noteCount, "note"),
    plural(visit.imageCount, "image"),
    plural(visit.invoiceCount, "invoice"),
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? "Nothing recorded" : parts.join(" · ");
}

function plural(count: number, noun: string): string | null {
  if (count === 0) return null;
  return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}

function regionLabel(region: string | null): string {
  if (region === null) return "";
  return region === "FULL_MOUTH" ? "Full mouth" : `Tooth ${region}`;
}

/** A local date string the server resolved, rendered in the reader's locale. */
export function formatDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
